/*
 * Юридические тексты ЛК: согласие на обработку ПДн, согласие на рассылки,
 * ссылка на политику. Источник — тексты от 26.10.2026 (Rinat G).
 *
 * С 01.09.2025 согласие на обработку ПДн оформляется отдельно от других
 * документов (ч. 1 ст. 9 152-ФЗ в ред. 156-ФЗ) — поэтому это отдельный
 * документ со своей страницей (/consent/personal-data), а не пункт оферты.
 * При изменении текста — поднимать CONSENT_VERSION.
 */

export const CONSENT_VERSION = '2026-10-26';

export const PRIVACY_POLICY_URL =
  'https://www.fmeter.ru/upload/documents/privacy_policy.pdf';

export type LegalDocKind = 'personal-data' | 'marketing';

type Block = { p: string } | { list: string[] };

export interface LegalDocument {
  title: string;
  path: string;
  intro: string;
  sections: { heading: string; blocks: Block[] }[];
}

const OPERATOR =
  'ООО «ОЙЛ», ИНН 1655164731, ОГРН 1081690060341, адрес: 420000, Республика Татарстан, г. Казань, ул. Дементьева, д. 2б (далее — Оператор).';

const PROCESSORS = [
  'АО «Селектел» (ИНН 7810962785, ОГРН 1247800067790), адрес: 196006, г. Санкт-Петербург, ул. Цветочная, д. 21, лит. А — размещение и хранение данных;',
];

export const LEGAL_DOCUMENTS: Record<LegalDocKind, LegalDocument> = {
  'personal-data': {
    title: 'Согласие на обработку персональных данных',
    path: '/consent/personal-data',
    intro:
      'Я, регистрируясь в личном кабинете на сайте lk.fmeter.ru и в мобильных приложениях оператора (далее — Личный кабинет), свободно, своей волей и в своём интересе даю согласие на обработку моих персональных данных на условиях ниже, в соответствии со ст. 9 Федерального закона от 27.07.2006 № 152-ФЗ «О персональных данных».',
    sections: [
      { heading: '1. Оператор', blocks: [{ p: OPERATOR }] },
      {
        heading: '2. Цели обработки',
        blocks: [
          {
            list: [
              'регистрация и авторизация в Личном кабинете, ведение учётной записи, привязка её к организации, которую я представляю;',
              'оформление, оплата и доставка заказов, выставление счетов и закрывающих документов;',
              'техническая поддержка: приём и рассмотрение обращений, рекламаций и заявок на диагностику оборудования, в том числе с помощью ИИ-ассистента;',
              'направление служебных уведомлений по электронной почте и push-уведомлений о статусе заказов, обращений и работе учётной записи;',
              'анализ использования Личного кабинета и мобильных приложений для помощи в настройке и подключении оборудования, улучшения работы сервисов и начисления баллов бонусной программы;',
              'исполнение обязанностей Оператора по законодательству РФ, в том числе налоговому и бухгалтерскому учёту.',
            ],
          },
        ],
      },
      {
        heading: '3. Перечень персональных данных',
        blocks: [
          {
            list: [
              'фамилия, имя, отчество; номер телефона; адрес электронной почты; должность;',
              'наименование и реквизиты организации, юридический адрес и адрес доставки; ФИО и телефон получателя или доверенного лица для доставки;',
              'логин и сведения об учётной записи, история заказов и обращений, содержание переписки и приложенные файлы;',
              'сведения об оборудовании, которое я подключаю и настраиваю (тип, серийный номер, результаты подключения);',
              'технические данные: IP-адрес, сведения о браузере и устройстве, версия приложения, идентификатор установки и токен push-уведомлений, файлы cookie, дата и время действий в Личном кабинете и приложениях (открытые экраны, нажатия, изменения настроек, ошибки).',
            ],
          },
          { p: 'Специальные и биометрические персональные данные не обрабатываются.' },
        ],
      },
      {
        heading: '4. Действия и способы обработки',
        blocks: [
          {
            p: 'Сбор, запись, систематизация, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, передача (предоставление, доступ), обезличивание, блокирование, удаление, уничтожение. Обработка смешанная: с использованием средств автоматизации и без них, с передачей по сети Интернет. Решения, порождающие юридические последствия, на основании исключительно автоматизированной обработки не принимаются.',
          },
        ],
      },
      {
        heading: '5. Передача и поручение обработки',
        blocks: [
          { p: 'Оператор вправе поручить обработку следующим лицам:' },
          {
            list: [
              ...PROCESSORS,
              'ООО «Интернет-портал Мэйл.ру», адрес: 109004, г. Москва, ул. Земляной Вал, д. 8, корп. 3 — отправка и приём электронной почты;',
              'транспортные компании и службы доставки, выбранные мной при оформлении заказа, — доставка (ФИО, телефон, адрес).',
            ],
          },
        ],
      },
      {
        heading: '6. Срок действия',
        blocks: [
          {
            p: 'Согласие действует с момента регистрации до удаления учётной записи и в течение 3 лет после этого либо до его отзыва. Данные, которые Оператор обязан хранить по закону (первичные учётные документы), хранятся в течение установленных законом сроков.',
          },
        ],
      },
      {
        heading: '7. Отзыв согласия',
        blocks: [
          {
            p: 'Я могу отозвать согласие, направив заявление на адрес электронной почты info@fmeter.ru или письмом по адресу: 420036, Республика Татарстан, г. Казань, а/я 209. Оператор прекращает обработку и уничтожает данные в течение 30 дней с даты получения отзыва, если иное не предусмотрено законом. После отзыва пользование Личным кабинетом становится невозможным. Оператор вправе продолжить обработку без согласия в случаях, предусмотренных п. 2–11 ч. 1 ст. 6 152-ФЗ.',
          },
        ],
      },
      {
        heading: '8. Прочее',
        blocks: [
          {
            p: `Порядок обработки и защиты данных и мои права как субъекта персональных данных описаны в Политике оператора в отношении обработки персональных данных: ${PRIVACY_POLICY_URL}. Согласие даётся в электронной форме проставлением отметки в форме регистрации; факт, дата и время его получения фиксируются в системе Оператора.`,
          },
        ],
      },
    ],
  },
  marketing: {
    title: 'Согласие на получение рекламных и информационных рассылок',
    path: '/consent/marketing',
    intro:
      'Я, регистрируясь в личном кабинете на сайте lk.fmeter.ru и в мобильных приложениях оператора (далее — Личный кабинет), свободно, своей волей и в своём интересе даю согласие на получение рекламных и информационных сообщений и на обработку для этого моих персональных данных в соответствии со ст. 9 и 15 Федерального закона от 27.07.2006 № 152-ФЗ «О персональных данных» и ст. 18 Федерального закона от 13.03.2006 № 38-ФЗ «О рекламе» на условиях ниже.',
    sections: [
      { heading: '1. Оператор', blocks: [{ p: OPERATOR }] },
      {
        heading: '2. Цель',
        blocks: [
          {
            p: 'Направление мне сообщений о товарах, услугах, акциях, новых функциях сервисов и бонусной программе Оператора, в том числе подобранных с учётом моих заказов и использования сервисов.',
          },
        ],
      },
      {
        heading: '3. Каналы',
        blocks: [
          {
            p: 'Электронная почта, push-уведомления в мобильных приложениях, SMS и мессенджеры по указанному мной номеру телефона.',
          },
        ],
      },
      {
        heading: '4. Перечень персональных данных',
        blocks: [
          {
            p: 'Фамилия, имя; номер телефона; адрес электронной почты; наименование организации; идентификатор установки и токен push-уведомлений мобильного приложения; сведения о заказах и использовании сервисов Оператора.',
          },
        ],
      },
      {
        heading: '5. Действия и способы обработки',
        blocks: [
          {
            p: 'Сбор, запись, систематизация, накопление, хранение, уточнение, извлечение, использование, передача (предоставление, доступ), блокирование, удаление, уничтожение. Обработка смешанная: с использованием средств автоматизации и без них, с передачей по сети Интернет.',
          },
        ],
      },
      {
        heading: '6. Поручение обработки',
        blocks: [
          { p: 'Оператор вправе поручить обработку следующим лицам:' },
          {
            list: [
              ...PROCESSORS,
              'ООО «Интернет-портал Мэйл.ру», адрес: 109004, г. Москва, ул. Земляной Вал, д. 8, корп. 3 — отправка электронных писем.',
            ],
          },
        ],
      },
      {
        heading: '7. Срок действия и отзыв',
        blocks: [
          {
            p: 'Согласие действует до отзыва. Отозвать его можно в настройках профиля Личного кабинета, по ссылке «Отписаться» в письме или заявлением на адрес info@fmeter.ru. Рассылки прекращаются незамедлительно, данные, использовавшиеся только для рассылок, уничтожаются в течение 30 дней.',
          },
        ],
      },
      {
        heading: '8. Прочее',
        blocks: [
          {
            p: `Согласие необязательно: отказ от него или его отзыв не влияет на работу Личного кабинета, оформление заказов и получение служебных уведомлений. Порядок обработки и защиты данных описан в Политике оператора в отношении обработки персональных данных: ${PRIVACY_POLICY_URL}. Согласие даётся в электронной форме отдельной отметкой в форме регистрации или в настройках профиля; факт, дата и время его получения и отзыва фиксируются в системе Оператора.`,
          },
        ],
      },
    ],
  },
};
